
/**
 * Card data module - exports all card collections and utility functions.
 */

// Re-export types (type-only to avoid runtime imports)
export type { Card, CardAbility, AbilityTrigger, TribeId } from './types';

// Import card collections
import { starterUnits } from './starterUnits';
import { neutralUnits } from './neutralUnits';
import { tokens } from './tokens';
import type { Card } from './types';
import { HeroId } from '../heroes';

// Re-export collections for direct access
export { starterUnits, neutralUnits, tokens };

/**
 * An array containing all card objects available in the game.
 */
export const allCards: Card[] = [
    ...starterUnits,
    ...neutralUnits,
    ...tokens,
];

/**
 * Pre-computed map of cards bucketed by tier for efficient shop generation.
 * Excludes starter units and tokens.
 */
export const cardsByTier: Record<number, Card[]> = (() => {
    const buckets: Record<number, Card[]> = {
        1: [],
        2: [],
        3: [],
        4: [],
        5: [],
        6: []
    };

    // Only include neutral units, not starter units or tokens
    for (const card of neutralUnits) {
        if (card.tier >= 1 && card.tier <= 6) {
            buckets[card.tier].push(card);
        }
    }

    return buckets;
})();

/**
 * Shuffles an array in place using the Fisher-Yates algorithm.
 * @param array The array to shuffle.
 */
function shuffleArray<T>(array: T[]): T[] {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]]; // Swap elements
  }
  return array;
}

/**
 * Generates a draft booster pack tailored to a specific hero.
 * @param {HeroId} heroId - The ID of the hero to generate the booster for.
 * @param {number} size - The number of cards to include in the booster.
 * @returns {Card[]} An array of card objects for the draft.
 */
export function getHeroBooster(heroId: HeroId, size: number = 6): Card[] {
  // 1. Get hero-specific starter cards
  const heroStarters = allCards.filter(card => card.starterForHeroId === heroId);

  // 2. Get neutral tier 1 cards (those without a hero association).
  // Tokens are tier 1 but are summon-only — they must never enter a booster.
  const neutralPool = neutralUnits.filter(
    card => !card.starterForHeroId && card.tier === 1,
  );

  // Shuffle both pools to randomize selection
  const shuffledHeroStarters = shuffleArray([...heroStarters]);
  const shuffledNeutrals = shuffleArray([...neutralPool]);

  // 3. Pick up to 3 cards from the hero's starter pool
  const numHeroCards = Math.min(shuffledHeroStarters.length, 3);
  const pickedHeroCards = shuffledHeroStarters.slice(0, numHeroCards);

  // 4. Fill the remaining slots from the neutral pool
  const remainingSize = size - pickedHeroCards.length;
  const pickedNeutralCards = shuffledNeutrals.slice(0, remainingSize);

  // 5. Combine the picked cards, shuffle the final result, and return it
  const finalBooster = [...pickedHeroCards, ...pickedNeutralCards];
  return shuffleArray(finalBooster);
}
